import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Badge,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type BadgeTone,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import {
  useRemoveStaffMutation,
  useSetStaffStatusMutation,
  useUpdatePermissionsMutation,
} from '../api';
import {
  PERMISSION_KEYS,
  PERMISSION_LABEL_KEY,
  type PermissionKey,
  type StaffMember,
} from '../types';

import { useToast } from '@/components/ui/toast/ToastProvider';

const STATUS_TONE: Record<string, BadgeTone> = { active: 'good', suspended: 'critical' };
const STATUS_LABEL_KEY: Record<string, MessageKey> = {
  active: 'merchant.products.status.active',
  suspended: 'merchant.staff.statusSuspended',
};

function reportError(
  error: unknown,
  toast: ReturnType<typeof useToast>,
  t: ReturnType<typeof useI18n>['t'],
): void {
  toast.error(t(resolveErrorMessageKey(error)));
}

export function StaffTable({
  members,
  onReissue,
}: {
  members: readonly StaffMember[];
  /** Opens the « new provisional password » flow for an account this shop created. */
  onReissue?: (member: StaffMember) => void;
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const updatePermissions = useUpdatePermissionsMutation();
  const setStatus = useSetStaffStatusMutation();
  const removeStaff = useRemoveStaffMutation();
  const [editingId, setEditingId] = useState<string | null>(null);

  async function onToggleStatus(member: StaffMember): Promise<void> {
    const next = member.status === 'active' ? 'suspended' : 'active';
    try {
      await setStatus.mutateAsync({ membershipId: member.id, status: next });
      toast.success(
        next === 'suspended'
          ? t('merchant.staff.suspendedToast')
          : t('merchant.staff.reactivatedToast'),
      );
    } catch (error) {
      reportError(error, toast, t);
    }
  }

  async function onRemove(member: StaffMember): Promise<void> {
    if (!window.confirm(t('merchant.staff.removeConfirm', { email: member.email }))) return;
    try {
      await removeStaff.mutateAsync(member.id);
      toast.success(t('merchant.staff.removedToast'));
    } catch (error) {
      reportError(error, toast, t);
    }
  }

  async function onSavePermissions(
    member: StaffMember,
    next: readonly PermissionKey[],
  ): Promise<void> {
    try {
      await updatePermissions.mutateAsync({ membershipId: member.id, permissions: next });
      toast.success(t('merchant.staff.permissionsUpdatedToast'));
      setEditingId(null);
    } catch (error) {
      reportError(error, toast, t);
    }
  }

  return (
    <Table caption={t('merchant.nav.team')}>
      <TableHead>
        <TableRow>
          <TableHeaderCell>{t('merchant.staff.table.colMember')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.staff.table.colRole')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.staff.table.colPermissions')}</TableHeaderCell>
          <TableHeaderCell> </TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {members.map((member) => {
          const isOwner = member.role_key === 'merchant_owner';
          const isEditing = editingId === member.id;
          const statusLabelKey = STATUS_LABEL_KEY[member.status];

          return (
            <TableRow key={member.id}>
              <TableCell>
                <span className="text-sm text-ink">{member.email}</span>
              </TableCell>
              <TableCell>
                <span className="text-sm text-inkSoft">
                  {isOwner ? t('merchant.staff.roleOwner') : t('merchant.staff.roleStaff')}
                </span>
              </TableCell>
              <TableCell>
                <Badge tone={STATUS_TONE[member.status] ?? 'neutral'}>
                  {statusLabelKey !== undefined ? t(statusLabelKey) : member.status}
                </Badge>
              </TableCell>
              <TableCell>
                {isOwner ? (
                  <span className="text-xs text-inkFaint">{t('merchant.staff.fullAccess')}</span>
                ) : isEditing ? (
                  <PermissionEditor
                    initial={member.permissions.filter((p): p is PermissionKey =>
                      (PERMISSION_KEYS as readonly string[]).includes(p),
                    )}
                    onCancel={() => {
                      setEditingId(null);
                    }}
                    onSave={(next) => {
                      void onSavePermissions(member, next);
                    }}
                    isSaving={updatePermissions.isPending}
                  />
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {member.permissions.length === 0 ? (
                      <span className="text-xs text-inkFaint">
                        {t('merchant.staff.noPermissions')}
                      </span>
                    ) : (
                      member.permissions.map((key) => {
                        const labelKey = (PERMISSION_LABEL_KEY as Record<string, MessageKey>)[key];
                        return (
                          <span
                            key={key}
                            className="rounded-full bg-surface2 px-2 py-0.5 text-[11px] text-inkSoft"
                          >
                            {labelKey !== undefined ? t(labelKey) : key}
                          </span>
                        );
                      })
                    )}
                    <button
                      type="button"
                      className="text-xs text-accent hover:underline"
                      onClick={() => {
                        setEditingId(member.id);
                      }}
                    >
                      {t('merchant.common.edit')}
                    </button>
                  </div>
                )}
              </TableCell>
              <TableCell align="end">
                {!isOwner ? (
                  <div className="flex justify-end gap-2">
                    {member.managed && onReissue !== undefined ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          onReissue(member);
                        }}
                      >
                        {t('merchant.staff.reissue')}
                      </Button>
                    ) : null}
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={setStatus.isPending}
                      onClick={() => {
                        void onToggleStatus(member);
                      }}
                    >
                      {member.status === 'active'
                        ? t('merchant.staff.suspendAction')
                        : t('merchant.staff.reactivateAction')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      loading={removeStaff.isPending}
                      onClick={() => {
                        void onRemove(member);
                      }}
                    >
                      {t('merchant.staff.removeAction')}
                    </Button>
                  </div>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function PermissionEditor({
  initial,
  onSave,
  onCancel,
  isSaving,
}: {
  initial: readonly PermissionKey[];
  onSave: (next: readonly PermissionKey[]) => void;
  onCancel: () => void;
  isSaving: boolean;
}): ReactElement {
  const { t } = useI18n();
  const [selected, setSelected] = useState<readonly PermissionKey[]>(initial);

  return (
    <div className="flex flex-col gap-1.5">
      {PERMISSION_KEYS.map((key) => (
        <Checkbox
          key={key}
          label={t(PERMISSION_LABEL_KEY[key])}
          checked={selected.includes(key)}
          onChange={(checked) => {
            setSelected((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
          }}
        />
      ))}
      <div className="mt-1 flex gap-2">
        <Button
          size="sm"
          loading={isSaving}
          onClick={() => {
            onSave(selected);
          }}
        >
          {t('merchant.common.save')}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>
          {t('merchant.common.cancel')}
        </Button>
      </div>
    </div>
  );
}
